// Constants
export const SizeType = {
  SCALE_TO_FILL: { name: 'Scale to fill', class: 'cover' },
  ORIGINAL_SIZE: { name: 'Original size', class: 'auto' },
  TILE: { name: 'Tile', class: 'contain' },
}

// Color conversion utility
export const hexToRGBA = (hex, opacity) => {
  try {
    hex = hex.replace('#', '')

    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(char => char + char)
        .join('')
    }
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    const validOpacity = Math.min(Math.max(opacity, 0), 1)

    return `rgba(${r}, ${g}, ${b}, ${validOpacity})`
  } catch (error) {
    console.error('Error converting hex to RGBA:', error)
    return 'rgba(0, 0, 0, 1)'
  }
}

// Editor update utilities
export const updateModelAttribute = (
  editor,
  selection,
  attributeName,
  value,
) => {
  if (!selection) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      writer.setAttribute(attributeName, value, modelElement)
    }
  })
}

export const updateViewStyle = (editor, selection, styleName, value) => {
  if (!selection) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle(styleName, value, viewElement)
        })
      }
    }
  })
}
export const handleButtonWidth = (editor, selection, newSizing) => {
  if (!selection || newSizing === null) return

  const buttonWrapper = selection.getChild(0).getChild(0).getChild(0)
  if (buttonWrapper) {
    editor.model.change(writer => {
      const modelElement = editor.editing.mapper.toModelElement(buttonWrapper)
      if (modelElement) {
        writer.setAttribute('dataSizing', newSizing, modelElement)
      }
    })
  }
}

export const handleContainerWidth = (editor, selection, newSizing) => {
  if (!selection || newSizing === null) return

  const buttonWrapper = selection
  if (buttonWrapper) {
    editor.model.change(writer => {
      const modelElement = editor.editing.mapper.toModelElement(buttonWrapper)
      if (modelElement) {
        writer.setAttribute('dataSizing', newSizing, modelElement)
      }
    })
  }
}
// Handler functions
export const handleAlignment = (editor, selection, newAlignment) => {
  if (newAlignment === null) return

  const buttonWrapper = selection.getChild(0).getChild(0).getChild(0)
  if (buttonWrapper) {
    editor.model.change(writer => {
      const modelElement = editor.editing.mapper.toModelElement(buttonWrapper)
      if (modelElement) {
        writer.setAttribute('dataAlignment', newAlignment, modelElement)
      }
    })
  }
}

export const handleBackground = (editor, selection, newBackground) => {
  if (newBackground === null) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      if (newBackground === 'color') {
        writer.setAttribute('backgroundImage', '', modelElement)
      } else {
        writer.setAttribute('backgroundColor', '', modelElement)
      }

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          if (newBackground === 'color') {
            writer.setStyle('background-image', '', viewElement)
          } else {
            writer.setStyle('background-color', '', viewElement)
          }
        })
      }
    }
  })

  updateModelAttribute(editor, selection, 'backgroundType', newBackground)
}

export const handleSize = (editor, selection, selectedSize) => {
  if (!selection) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      writer.setAttribute(
        'backgroundSize',
        SizeType[selectedSize].class,
        modelElement,
      )
      writer.setAttribute('sizeType', selectedSize, modelElement)

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle(
            'background-size',
            SizeType[selectedSize].class,
            viewElement,
          )
          if (selectedSize === 'ORIGINAL_SIZE') {
            writer.setStyle('background-repeat', 'no-repeat', viewElement)
          } else {
            writer.removeStyle('background-repeat', viewElement)
          }
        })
      }
    }
  })
}

export const handleImageDelete = (editor, selection) => {
  if (!selection) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      writer.setAttribute('backgroundImage', '', modelElement)
      writer.setAttribute('backgroundColor', '', modelElement)
      writer.setAttribute('backgroundSize', '', modelElement)
      writer.setAttribute('sizeType', 'SCALE_TO_FILL', modelElement)
      writer.setAttribute('backgroundPosition', 'center', modelElement)

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle('background-image', '', viewElement)
        })
      }
    }
  })
}

export const handleOverlayColor = (
  editor,
  selection,
  color,
  opacityValue,
  imageURL,
) => {
  if (!selection || color === null) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      const rgbaColor = hexToRGBA(color, opacityValue)
      writer.setAttribute('overlayColor', color, modelElement)
      writer.setAttribute('linearValue', rgbaColor, modelElement)

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle(
            'background-image',
            `linear-gradient(${rgbaColor},${rgbaColor}) ,url(${imageURL})`,
            viewElement,
          )
        })
      }
    }
  })
}

export const handlePosition = (editor, selection, position) => {
  if (!selection || position === null) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      writer.setAttribute('backgroundPosition', position, modelElement)

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle('background-position', position, viewElement)
        })
      }
    }
  })
}

export const handleColor = (editor, selection, color) => {
  if (!selection || color === null) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      writer.setAttribute('backgroundColor', color, modelElement)
      writer.setAttribute('backgroundImage', '', modelElement)

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(writer => {
          writer.setStyle('background-color', color, viewElement)
          writer.setStyle('background-image', '', viewElement)
        })
      }
    }
  })
}

export const handleImageOverlay = (editor, selection, isChecked) => {
  if (!selection) return

  editor.model.change(writer => {
    const modelElement = editor.editing.mapper.toModelElement(selection)
    if (modelElement) {
      writer.setAttribute('useImageOverlay', !isChecked, modelElement)
    }
  })
}
